import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import config from "@/config";
import { useIsSizeRecommendationEnabled } from "@/hooks/use-is-size-recommendation-enabled";
import { tokenManager } from "@babackapp/auth-client/utils";
import { Badge } from "@babackapp/ui/components/badge";
import { Button } from "@babackapp/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@babackapp/ui/components/card";
import { Input } from "@babackapp/ui/components/input";
import { Label } from "@babackapp/ui/components/label";
import { toast } from "@babackapp/ui/components/sonner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate } from "react-router-dom";
import type { ReactNode } from "react";
import { useState } from "react";

type SizeGuideAlgorithm = "morphological" | "table_lookup";
type SizeGuideRuleAttribute = "tag" | "productType" | "metafield";

type SizeGuideRule = {
  attribute: SizeGuideRuleAttribute;
  metafieldKey?: string;
  values: string[];
};

type SizeGuideMappingEntry = {
  size: string;
  ranges: Record<string, { min: number; max: number }>;
};

type SizeGuide = {
  id: string;
  name: string;
  rules: SizeGuideRule[];
  questions: string[];
  algorithm: SizeGuideAlgorithm;
  mappings: SizeGuideMappingEntry[] | null;
};

const DEFAULT_RULES = JSON.stringify(
  [{ attribute: "tag", values: ["lingerie"] }],
  null,
  2,
);
const DEFAULT_QUESTIONS = "gender,height,weight,fit_preference";
const DEFAULT_MAPPINGS = JSON.stringify(
  [
    {
      size: "M",
      ranges: {
        bust: { min: 88, max: 96 },
        underbust: { min: 76, max: 84 },
      },
    },
  ],
  null,
  2,
);

export default function SizingGuidesPage() {
  const { data: shop } = useCurrentShopSuspense();
  const isSizingEnabled = useIsSizeRecommendationEnabled();

  if (!isSizingEnabled) {
    return <Navigate to={`/shops/${shop.id}`} replace />;
  }

  return (
    <div className="flex items-center justify-center">
      <div className="grid grid-cols-4 gap-4 w-full max-w-[1600px] lg:px-16 lg:py-6 px-6 py-4">
        <div className="col-span-4 mb-4 space-y-2">
          <h2 className="text-2xl leading-6 font-semibold text-foreground">
            Guides de taille
          </h2>
          <p className="text-sm leading-relaxed text-muted-foreground max-w-[720px]">
            Configurez les guides, leurs règles de routing et les questions
            posées dans le widget sizing.
          </p>
        </div>

        <div className="col-span-4 xl:col-span-3">
          <SizeGuidesList shopId={shop.id} />
        </div>
        <div className="col-span-4 xl:col-span-1">
          <SizeGuideForm shopId={shop.id} />
        </div>
      </div>
    </div>
  );
}

function SizeGuidesList({ shopId }: { shopId: string }) {
  const queryClient = useQueryClient();
  const guidesQuery = useQuery({
    queryKey: ["size-guides", shopId],
    queryFn: () => requestSizeGuides(shopId),
  });
  const deleteGuide = useMutation({
    mutationFn: (guideId: string) => deleteSizeGuide(shopId, guideId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["size-guides", shopId],
      });
      toast.success("Guide supprimé");
    },
    onError: () => toast.error("Impossible de supprimer le guide"),
  });

  if (guidesQuery.isLoading) {
    return (
      <Card>
        <CardContent className="p-6">Chargement…</CardContent>
      </Card>
    );
  }

  if (guidesQuery.isError) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-destructive">
          Impossible de charger les guides.
        </CardContent>
      </Card>
    );
  }

  const guides = guidesQuery.data ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Guides configurés</CardTitle>
        <CardDescription>
          Les guides sont évalués par ordre de création. Le premier match gagne.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {guides.length === 0 ? (
          <div className="rounded-md border border-dashed p-6 text-sm text-muted-foreground">
            Aucun guide configuré. Le widget utilise le guide morphologique par
            défaut.
          </div>
        ) : (
          guides.map((guide) => (
            <div
              key={guide.id}
              className="rounded-md border p-4 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between"
            >
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <h3 className="font-medium">{guide.name}</h3>
                  <Badge variant="secondary">{guide.algorithm}</Badge>
                </div>
                <div className="text-sm text-muted-foreground">
                  Questions: {guide.questions.join(", ")}
                </div>
                <div className="text-sm text-muted-foreground">
                  Règles:{" "}
                  {guide.rules
                    .map((rule) => `${rule.attribute}=${rule.values.join("|")}`)
                    .join(", ")}
                </div>
              </div>
              <Button
                variant="outline"
                disabled={deleteGuide.isPending}
                onClick={() => deleteGuide.mutate(guide.id)}
              >
                Supprimer
              </Button>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}

function SizeGuideForm({ shopId }: { shopId: string }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [algorithm, setAlgorithm] =
    useState<SizeGuideAlgorithm>("morphological");
  const [questions, setQuestions] = useState(DEFAULT_QUESTIONS);
  const [rules, setRules] = useState(DEFAULT_RULES);
  const [mappings, setMappings] = useState(DEFAULT_MAPPINGS);

  const createGuide = useMutation({
    mutationFn: () =>
      createSizeGuide(shopId, {
        name,
        algorithm,
        questions: parseCsv(questions),
        rules: JSON.parse(rules) as SizeGuideRule[],
        mappings:
          algorithm === "table_lookup"
            ? (JSON.parse(mappings) as SizeGuideMappingEntry[])
            : null,
      }),
    onSuccess: async () => {
      setName("");
      await queryClient.invalidateQueries({
        queryKey: ["size-guides", shopId],
      });
      toast.success("Guide créé");
    },
    onError: () => toast.error("Configuration invalide"),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Nouveau guide</CardTitle>
        <CardDescription>
          Les règles et mappings sont saisis en JSON pour cette première
          version.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Field label="Nom">
          <Input
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>

        <Field label="Algorithme">
          <select
            className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            value={algorithm}
            onChange={(event) =>
              setAlgorithm(event.target.value as SizeGuideAlgorithm)
            }
          >
            <option value="morphological">morphological</option>
            <option value="table_lookup">table_lookup</option>
          </select>
        </Field>

        <Field label="Questions">
          <Input
            value={questions}
            onChange={(event) => setQuestions(event.target.value)}
          />
        </Field>

        <Field label="Règles JSON">
          <textarea
            className="min-h-28 w-full rounded-md border bg-background px-3 py-2 text-sm font-mono"
            value={rules}
            onChange={(event) => setRules(event.target.value)}
          />
        </Field>

        {algorithm === "table_lookup" && (
          <Field label="Mappings JSON">
            <textarea
              className="min-h-40 w-full rounded-md border bg-background px-3 py-2 text-sm font-mono"
              value={mappings}
              onChange={(event) => setMappings(event.target.value)}
            />
          </Field>
        )}

        <Button
          className="w-full"
          disabled={!name.trim() || createGuide.isPending}
          onClick={() => createGuide.mutate()}
        >
          Créer
        </Button>
      </CardContent>
    </Card>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {children}
    </div>
  );
}

function parseCsv(value: string): string[] {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

async function requestSizeGuides(shopId: string): Promise<SizeGuide[]> {
  return request(`/shops/${shopId}/sizing/guides`);
}

async function createSizeGuide(
  shopId: string,
  body: Omit<SizeGuide, "id">,
): Promise<SizeGuide> {
  return request(`/shops/${shopId}/sizing/guides`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

async function deleteSizeGuide(shopId: string, guideId: string): Promise<void> {
  await request(`/shops/${shopId}/sizing/guides/${guideId}`, {
    method: "DELETE",
  });
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const accessToken = tokenManager.getAccessToken();
  const response = await fetch(`${config.api.url}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...init?.headers,
    },
  });

  if (!response.ok) {
    throw new Error(`Request failed with ${response.status}`);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}
